<template>
  <section id="tasks">
    <header v-bind:class="{'warning': incomplete}">
      <div class="pull-right subtitle" style="margin-top: 3px" v-if="loaded">{{remaining}} Remaining</div>
      <h2>
        Tasks
        <span style="font-weight: normal; font-size: .7em" v-if="waiting">Saving...</span>
      </h2>
    </header>
    <div class="task_list">
      <div class="form-child" v-for="(task, i) in tasks" v-bind:class="{'complete': task.completed, 'incomplete': !task.completed, 'flagged': task.flagged}" :key="task.id">
        <div class="child-action">
          <tooltip :text="completedAt(task)" placement="top" :enable="task.completed">
            <i
              class="far fa-lg icon-btn"
              :class="{
                'fa-square': !task.completed,
                'fa-check-square': task.completed,
                'locked': locked,
                'disabled': waiting
              }"
              v-on:click="toggle(task)"
            />
          </tooltip>
        </div>
        <div class="child-content">
          <div class="flags" v-if="task.flagged || (canFlag && task.responseType === 'task')">
            <tooltip :text="flagTooltip(task)" placement="top">
              <i
                class="fas fa-flag-alt icon-btn"
                :class="{
                  'icon-flagged': task.flagged,
                  'icon-flag': !task.flagged,
                  'unlocked': (canFlag),
                  'disabled': waiting
                }"
                v-on:click="toggleFlag(task)"
              />
            </tooltip>
          </div>
          <div v-if="task.form" style="padding-bottom: 6px">
            <a :href="task.formUrl" @click.prevent="openForm(task, false)" style="font-weight: bold">{{ task.form }}</a>
            <span style="color: #999" v-if="task.remaining > 0">{{task.remaining}} Remaining</span>
          </div>
          <div class="document-body" v-html="task.description" @click.prevent=externalContentClick></div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import { EventBus } from '../mixins/events.js'
  import axios from '../mixins/axiosDefaults'
  import { Tooltip } from 'uiv'

  export default {
    props: {
      memberId: {
        type: Number,
        required: false
      },
      baseUrl: {
        type: String,
        required: true
      },
      readOnly: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    components: {
      Tooltip
    },
    data: function () {
      return {
        tasks: [],
        unlocked: false,
        canFlag: false,
        loaded: false,
        waiting: false
      }
    },
    computed: {
      remaining() {
        let remainingCount = this.tasks.filter(task => !task.completed || task.flagged).length;
        this.$emit('is-complete', remainingCount == 0)
        return remainingCount;
      },
      incomplete(){
        return this.loaded && this.remaining > 0;
      },
      locked() {
        const locked = this.readOnly || !this.unlocked
        return locked;
      }
    },
    created() {
      this.refreshPerms();
      this.loadTasks();
      EventBus.$on('formResponseUpdated', () => {
        console.log('refreshing tasks')
        this.loadTasks();
      });
    },
    methods: {
      completedAt(task) {
        let date = task.completedAt;
        if (date) {
          return `Completed ${moment(date).format('ddd MMM Do, YYYY [at] h:mm:ss A')}`
        }
      },
      flagTooltip(task){
        if (task.flagged){
          return (this.canFlag) ? 'Needs attention. Click to unflag.' : 'Flagged as incomplete. Please contact us for further information.';
        } else {
          return 'Click to flag if needs attention.'
        }
      },
      // Check if permissions changed at any time - mainly on error, assuming state changed
      refreshPerms() {
        if (!this.memberId) return;

        axios.get(this.baseUrl + '/responses/' + this.memberId + '.json')
          .then(response => {
            const { data } = response;
            this.unlocked = data.meta.unlocked;
            this.canFlag = data.meta.canFlag;
          });
      },
      loadTasks() {
        let taskUrl = this.baseUrl + '/tasks'
        if (this.memberId) taskUrl += '?response_id=' + this.memberId;
        axios.get(taskUrl)
          .then(response => {
            this.tasks = response.data;
            this.loaded = true;
            this.$emit('loaded');
          })
          .catch(this.requestErrorHandler);
      },
      toggle(task) {
        if (!this.locked && task.responseType === 'task') {
          let action = (task.completed) ? 'incomplete' : 'complete';
          this.updateTask(task, action);
        } else if (task.responseType === 'form') {
          this.openForm(task, false);
        }
      },
      toggleFlag(task) {
        if (this.canFlag) {
          let action = (task.flagged) ? 'unflag' : 'flag';
          this.updateTask(task, action);
        }
      },
      updateTask(task, action) {
        if (this.waiting) return; // Wait until last update finishes

        this.waiting = true;

        axios.post(this.baseUrl + '/tasks/' + task.id + '/' + action + '?response_id=' + this.memberId)
          .then(response => {
            task.completed = response.data.completed;
            task.flagged = response.data.flagged;
            if (task.flagged) EventBus.$emit('taskFlagged');
          })
          .catch(this.requestErrorHandler)
          .then(() => { this.waiting = false })
      },
      requestErrorHandler(error) {
        if(error.request.status === 403) {
          console.log(error)
          this.refreshPerms();
        } else if (error.request.status !== 0) {
          console.log(error.request.status);
          console.log(error.request.responseText);
          alert('Something has gone wrong. Please contact support if it continues.')
        }
        this.waiting = false;
      },
      externalContentClick(event) {
        let { target } = event;
        while (target && target.tagName !== 'A') target = target.parentNode;
        window.open(target.href, '_blank');
      },
      openForm(task, siblings) {
        if (task.formUrl.includes('answers')) {
          EventBus.$emit('openForm', task.formUrl, siblings)
        } else {
          window.location = task.formUrl
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  i.locked {
    cursor: not-allowed;
  }
  i.disabled {
    cursor: progress !important;
  }

  .fa-square {
    color: #999;
    background: #fff;
  }

  .fa-check-square {
    color: #555;
    background: #fff;
  }

  .icon-flag, .icon-flagged {
    font-size: 1.2em
  }
</style>
